import React from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { CHAT_STATUS, DateTime } from '@whatsper/texterchat-common';
import { ApartmentOutlined } from '@ant-design/icons';
import PanelSummary from '../components/PanelSummary';
import { withStore } from '../../../store/rootStore';
import { formatAsPercent } from '../../BulkReportsDrawer/helpers';
import { formatDaySecondsAsTime } from '../../../helpers/time';
import { ReactComponent as CheckSquare } from '../../../assets/icons/dashboard/CheckSquare.svg';
import { ReactComponent as UserAdd } from '../../../assets/icons/dashboard/UserAdd.svg';
import { ReactComponent as Dashboard } from '../../../assets/icons/dashboard/Dashboard.svg';
import { ReactComponent as ClockCircle } from '../../../assets/icons/dashboard/ClockCircle.svg';
import s from '../DashboardDrawer.module.scss';
const FALLBACK_WORKING_HOURS = DateTime.WEEKDAY_NAMES_LIST.reduce((schedule, _, index) => {
    schedule[index] = [{ begin: 9 * 60 * 60, end: 18 * 60 * 60 }];
    return schedule;
}, {});
const hasWorkingHours = (workingHours) => {
    return !!workingHours && Object.values(workingHours).some((daySchedule) => !!(daySchedule === null || daySchedule === void 0 ? void 0 : daySchedule.length));
};
const getBotWorkingHours = (workingHoursData) => {
    const days = workingHoursData.days;
    if (!days)
        return FALLBACK_WORKING_HOURS;
    // Bot working hours come from stats settings as weekday names in minutes
    // Normalize to department schedule shape and fall back to 09:00-18:00 if bot hours are missing
    const schedule = DateTime.WEEKDAY_NAMES_LIST.reduce((result, dayName, index) => {
        const daySchedule = days[dayName];
        if (daySchedule) {
            result[index] = [{ begin: daySchedule.start * 60, end: daySchedule.end * 60 }];
        }
        return result;
    }, {});
    return hasWorkingHours(schedule) ? schedule : FALLBACK_WORKING_HOURS;
};
const formatWorkingHours = (workingHours, currentTime) => {
    const daySchedule = workingHours === null || workingHours === void 0 ? void 0 : workingHours[currentTime.getDay()];
    if (!(daySchedule === null || daySchedule === void 0 ? void 0 : daySchedule.length))
        return undefined;
    return daySchedule
        .map(({ begin, end }) => `${formatDaySecondsAsTime(begin)} - ${formatDaySecondsAsTime(end)}`)
        .join(', ');
};
const Departments = ({ store: { departmentsStore, dashboard } }) => {
    const { t } = useTranslation();
    const { [CHAT_STATUS.PENDING]: pending, [CHAT_STATUS.ASSIGNED]: assigned } = dashboard.data;
    const currentTime = new Date();
    const botWorkingHours = getBotWorkingHours(dashboard.settingsWorkingHoursData);
    const departments = [
        ...departmentsStore.departments,
        {
            _id: null,
            name: t('withoutDepartment'),
            color: '#D9B64C', // primary yellow
            workingHours: undefined,
        },
    ]
        .map((department, index) => {
        // Departments prefer their configured/default department hours
        // Undefined department hours and "Without Department" use botWorkingHours
        const workingHours = hasWorkingHours(department.workingHours)
            ? department.workingHours
            : botWorkingHours;
        return Object.assign(Object.assign({}, department), { index, isActive: !!workingHours && !!DateTime.Match.weekSchedule(workingHours, currentTime), workingHoursLabel: formatWorkingHours(workingHours, currentTime) || t('notAvailable') });
    })
        // Active departments first, otherwise keep the configured order
        .sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.index - b.index);
    const columns = [
        { icon: React.createElement(ApartmentOutlined, null), title: t('department') },
        { icon: React.createElement(ClockCircle, null), title: t('pending') },
        { icon: React.createElement(UserAdd, null), title: t('taken') },
        { icon: React.createElement(CheckSquare, null), title: t('resolved') },
        { icon: React.createElement(Dashboard, null), title: t('SLA') },
    ];
    return (React.createElement("section", null,
        React.createElement("table", { className: classnames(s['main__panel--all'], s['main__panel--rowList']) },
            React.createElement("thead", null,
                React.createElement("tr", null, columns.map(({ icon, title }) => (React.createElement("th", { key: title },
                    React.createElement("div", { className: s['main__tableHeaderCell'] },
                        icon && React.createElement("span", { className: s['main__tableHeaderIcon'] }, icon),
                        React.createElement("span", null, title))))))),
            React.createElement("tbody", null, departments.map(({ name, _id, color, isActive, workingHoursLabel }, i) => {
                var _a, _b;
                const pendingData = pending === null || pending === void 0 ? void 0 : pending.departments.find((d) => d._id === _id);
                const assignedData = assigned === null || assigned === void 0 ? void 0 : assigned.departments.find((d) => d._id === _id);
                const resolvedData = (_a = dashboard.resolvedData) === null || _a === void 0 ? void 0 : _a.departments.find((d) => d._id === _id);
                const SLAData = (_b = dashboard.departmentSlaData) === null || _b === void 0 ? void 0 : _b.find((d) => d._id === _id);
                const { chatsCount: totalPending = 0 } = pendingData || {};
                const { chatsCount: totalTaken = 0 } = assignedData || {};
                const { agent_resolved = 0 } = resolvedData || {};
                const slaSuccess = (SLAData === null || SLAData === void 0 ? void 0 : SLAData.success) || 0;
                const slaTotal = slaSuccess + ((SLAData === null || SLAData === void 0 ? void 0 : SLAData.failure) || 0);
                const slaPercent = slaTotal ? slaSuccess / slaTotal : 0;
                return (React.createElement("tr", { key: _id || i, className: s['main__panel--row'] },
                    React.createElement("td", { className: s['main__panel--rowTitle'] },
                        React.createElement("div", { className: s['main__panel--departmentHeader'] },
                            _id !== null && (React.createElement("span", { className: classnames(s['main__panel--statusDot'], {
                                    [s['main__panel--statusDotMuted']]: !isActive,
                                }) })),
                            React.createElement("div", { className: s['main__panel--departmentLabel'] },
                                React.createElement("div", { className: s['main__panel--name'], style: { color } }, name),
                                React.createElement("div", { className: s['main__panel--workingHours'], dir: "ltr" }, workingHoursLabel)))),
                    React.createElement(PanelSummary, { panels: [
                            { title: t('pending'), value: totalPending },
                            { title: t('taken'), value: totalTaken },
                            {
                                title: t('resolved'),
                                value: agent_resolved,
                            },
                            {
                                title: t('SLA'),
                                value: formatAsPercent(slaPercent * 100),
                            },
                        ] })));
            })))));
};
export default withStore(observer(Departments));
